"use client";

import { useState, useEffect } from "react";
import { X } from "lucide-react";
import useCookieConsent from "../../hooks/useCookieConsent"; 
import { motion, AnimatePresence } from "framer-motion";
import { useTranslations } from "next-intl";


export default function CookieModal({ onClose }: { onClose: () => void }) {
  const [preferences, setPreferences] = useState({
    essential: true,
    functional: false,
    analytics: false,
    marketing: false,
  });
  const { updateConsent } = useCookieConsent();
  const t = useTranslations("CookieComponents.CookieModal");

  // Disable background scroll
  useEffect(() => {
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = original;
    };
  }, []);

  // Handle ESC key close
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  const handleChange = (type: string) => {
    if (type === "essential") return; // can't disable essential
    setPreferences((prev) => ({ ...prev, [type]: !prev[type as keyof typeof prev] }));
  };

  const acceptAll = () => {
    const allTrue = Object.fromEntries(
      Object.entries(preferences).map(([key]) => [key, true])
    );
    setPreferences(allTrue as any);
  };

  const rejectAll = () => {
    const essential = Object.fromEntries(
      Object.entries(preferences).map(([key]) => [key, (key != "essential") ? false : true] )
    );
    setPreferences(essential as any);
  };

  const confirm = () => {
    const allowed = Object.entries(preferences)
      .filter(([_, allowed]) => allowed)
      .map(([key]) => key)
      .join(","); 
    updateConsent(allowed)
    onClose();
  };

  return (
    <AnimatePresence>
        <motion.div
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 60, opacity: 0 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          className="relative bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex justify-between items-center border-b px-6 py-4">
            <h2 className="text-lg font-semibold text-gray-900">{t("title")}</h2>
            <button
              onClick={onClose}
              aria-label="Close cookie settings"
              className="p-2 rounded-full hover:bg-gray-100 transition"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
          </div>

          {/* Content */}
          <div className="px-6 py-4 space-y-4 text-sm text-gray-700 overflow-y-auto max-h-[70vh]">
            <p className="text-gray-600">{t("intro")}</p>

            {/* Buttons */}
            <div className="flex gap-3 mt-4">
              <button
                onClick={rejectAll}
                className="px-6 py-2 text-sm rounded-sm border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
              >
                <span className="font-button font-[number:var(--button-font-weight)] [font-style:var(--button-font-style)] text-gray-700 ">{t("reject")}</span>  
              </button>
              <button
                onClick={acceptAll}
                className="px-6 py-2 bg-brand-gradient text-sm text-white rounded-sm transition"
              >
                <span className="font-button font-[number:var(--button-font-weight)] [font-style:var(--button-font-style)]">{t("accept")}</span>  
              </button>
            </div>

            {/* Preferences List */}
            <div className="mt-6 space-y-4">
              {["essential", "functional", "analytics", "marketing"].map((key) => (
                <div
                  key={key}
                  className="flex items-start justify-between border rounded-lg p-4 hover:bg-gray-50 transition"
                >
                  <div className="pr-4">
                    <h3 className="font-medium text-gray-900">{t(`categories.${key}.title`)}</h3>
                    <p className="text-xs text-gray-600 mt-1">{t(`categories.${key}.desc`)}</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={preferences[key as keyof typeof preferences]}
                    onChange={() => handleChange(key)}
                    disabled={key === "essential"}
                    className="w-5 h-5 accent-[#3F74EF] cursor-pointer disabled:cursor-not-allowed"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Footer */}
          <div className="border-t px-6 py-4 flex justify-end gap-3 bg-gray-50">
            <button
              onClick={confirm}
              className="px-6 py-2 bg-brand-gradient text-sm text-white rounded-sm transition"
            >
              <span className="font-button font-[number:var(--button-font-weight)] [font-style:var(--button-font-style)]">{t("confirm")}</span>  
            </button>
          </div>
        </motion.div>
    </AnimatePresence>
  );
}
